/**
 * Part Number Display Utilities
 * After SKU Migration, parts have both `jp_part_no` (old) and `new_jp_part_no` (new JP series).
 * These helpers ensure the new number is shown everywhere with old in brackets.
 */

/**
 * Returns the display part number (new if available, else old)
 */
export function getDisplayPartNo(item) {
  if (!item) return '';
  return item.new_jp_part_no || item.jp_part_no || '';
}

/**
 * Returns the old part number only if different from new
 */
export function getOldPartNo(item) {
  if (!item) return null;
  if (item.new_jp_part_no && item.new_jp_part_no !== item.jp_part_no) {
    return item.jp_part_no;
  }
  return null;
}

/**
 * Returns a plain text string: "JPSNEW0001 (OLD-001)" or just "OLD-001" if no new number
 * Useful for toasts, sharing text, template literals
 */
export function formatPartNo(item) {
  if (!item) return '';
  const display = item.new_jp_part_no || item.jp_part_no || '';
  if (item.new_jp_part_no && item.new_jp_part_no !== item.jp_part_no) {
    return `${display} (${item.jp_part_no})`;
  }
  return display;
}

/**
 * Canonical "JP with alias in brackets" formatter — use this EVERYWHERE
 * a JP number is rendered in the UI (search results, picker rows, table
 * cells, price-request lists, etc).
 *
 * Bracket priority per product owner directive:
 *   1. `oe_part_no` (rendered via `formatOeBare`) — "200271-(ID)" style
 *   2. `legacy_jp_part_no` / `old_jp_part_no` / `jp_part_no` (only if
 *      different from the canonical JP being displayed)
 *   3. nothing — bare JP only
 *
 * Examples:
 *   JPSEV1282 + oe 21147687  → "JPSEV1282 (21147687)"
 *   JPSEV0279 + legacy JIA200001P  → "JPSEV0279 (JIA200001P)"
 *   JPSXY9999 (no OE, no legacy)    → "JPSXY9999"
 */
export function formatJpWithAlias(item) {
  if (!item) return '';
  const display = item.new_jp_part_no || item.jp_part_no || '';
  if (!display) return '';

  // 1. Prefer OE — formatted via formatOeBare so it matches stickers/PDFs.
  if (item.oe_part_no && String(item.oe_part_no).trim() && String(item.oe_part_no).trim().toLowerCase() !== 'not available') {
    try {
      const bare = formatOeBare(item.oe_part_no);
      if (bare && bare !== '-') return `${display} (${bare})`;
    } catch { /* fall through */ }
    return `${display} (${item.oe_part_no})`;
  }
  // 2. Else legacy / old JP, but only if it differs from the displayed JP.
  const legacy = item.legacy_jp_part_no || item.old_jp_part_no
                  || (item.new_jp_part_no ? item.jp_part_no : '');
  if (legacy && legacy !== display) {
    return `${display} (${legacy})`;
  }
  // 3. Bare JP.
  return display;
}

/**
 * Transform OE part number for IPR-safe display.
 * Backend stores original (e.g. "MB486038"), frontend shows "Suited for Ref: 486038-(MB)"
 * 
 * @param {string} oe - Original OE part number from DB
 * @param {boolean} short - If true, returns "Ref: 486038-(MB)" (for stickers/compact)
 * @returns {string} Transformed display string
 */
export function formatOeDisplay(oe, short = false) {
  if (!oe || String(oe).trim().toLowerCase() === 'not available' || String(oe).trim() === '-' || String(oe).trim() === '') {
    return short ? 'Ref: N/A' : 'Not Available';
  }

  // Accept arrays as well as separator-delimited strings.
  const rawList = Array.isArray(oe)
    ? oe
    : String(oe).split(/[\s,;|/]+/);

  // Transform each token to "DIGITS-(PREFIX)" if it has an alpha prefix.
  const transformed = rawList
    .map((piece) => (piece == null ? '' : String(piece).trim()))
    .filter((piece) => {
      if (!piece) return false;
      const low = piece.toLowerCase();
      return low !== 'not' && low !== 'available' && low !== 'n/a' && piece !== '-';
    })
    .map((piece) => {
      // Match only alpha-prefix followed by a digit so words don't get
      // mangled (e.g. "Available" stays as "Available").
      const m = piece.match(/^([A-Za-z]+)(\d[A-Za-z0-9/-]*)$/);
      if (m) {
        return `${m[2]}-(${m[1].toUpperCase()})`;
      }
      return piece;
    });

  if (transformed.length === 0) {
    return short ? 'Ref: N/A' : 'Not Available';
  }

  const joined = transformed.join(', ');
  return short ? `Ref: ${joined}` : `Suited for Ref: ${joined}`;
}

/**
 * Bare OE display — no "Ref:" or "Suited for Ref:" prefix.
 * Returns "486038-(MB)" or "200271-(ID), 300340-(IM)" for multi-OE.
 * Used on stickers and other places where space is tight and there's
 * already an "OE" label printed alongside.
 */
export function formatOeBare(oe) {
  if (!oe || String(oe).trim().toLowerCase() === 'not available' || String(oe).trim() === '-' || String(oe).trim() === '') {
    return 'N/A';
  }
  const rawList = Array.isArray(oe) ? oe : String(oe).split(/[\s,;|/]+/);
  const transformed = rawList
    .map((piece) => (piece == null ? '' : String(piece).trim()))
    .filter((piece) => {
      if (!piece) return false;
      const low = piece.toLowerCase();
      return low !== 'not' && low !== 'available' && low !== 'n/a' && piece !== '-';
    })
    .map((piece) => {
      const m = piece.match(/^([A-Za-z]+)(\d[A-Za-z0-9/-]*)$/);
      if (m) return `${m[2]}-(${m[1].toUpperCase()})`;
      return piece;
    });
  return transformed.length === 0 ? 'N/A' : transformed.join(', ');
}
